import { defineStore, acceptHMRUpdate } from 'pinia';
import keyBy from 'lodash/keyBy';
import isEmpty from 'lodash/isEmpty';
import { fetchFeatureFlags, toggleFeatureFlag, createFeatureFlag, updateFeatureFlag, deleteFeatureFlag } from '@/vuex/modules/feature-flags/feature-flags.utils';

export interface FeatureFlag {
    flagName: string;
    [key: string]: unknown;
}

interface FeatureFlagsState {
    map: Record<string, FeatureFlag>;
    error: unknown;
}

export function getInitialState (): FeatureFlagsState {
    return {
        map: {},
        error: null,
    };
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
    state: getInitialState,
    actions: {
        setMap (flags: FeatureFlag[]) {
            this.map = keyBy(flags, 'flagName') as Record<string, FeatureFlag>;
        },
        setMapAtKey ({ id, flag }: { id: string; flag: FeatureFlag }) {
            this.map[id] = flag;
        },
        setError ({ error }: { error: unknown }) {
            this.error = error;
        },
        async loadAll ({ reFetch = false }: { reFetch?: boolean } = {}) {
            if (!isEmpty(this.map) && !reFetch) {
                return;
            }

            const flags = await fetchFeatureFlags();
            this.setMap(flags);
        },
        async createFeatureFlag ({ featureFlag }: { featureFlag: object }) {
            try {
                this.setError({ error: null });
                const flag = await createFeatureFlag(featureFlag);
                this.setMapAtKey({ id: flag.flagName, flag });

                return flag;
            } catch (error) {
                this.setError({ error });
                console.error(error);
                throw error;
            }
        },
        async toggle ({ flagName, enabled }: { flagName: string; enabled: boolean }) {
            const flag = await toggleFeatureFlag(flagName, enabled);
            this.setMapAtKey({ id: flagName, flag });
        },
        async updateFeatureFlag ({ featureFlag }: { featureFlag: object }) {
            try {
                this.setError({ error: null });
                const flag = await updateFeatureFlag(featureFlag);
                this.setMapAtKey({ id: flag.flagName, flag });
            } catch (error) {
                this.setError({ error });
                console.error(error);
                throw error;
            }
        },
        async deleteFeatureFlag ({ flagName }: { flagName: string }) {
            try {
                this.setError({ error: null });
                await deleteFeatureFlag(flagName);
                delete this.map[flagName];
            } catch (error) {
                this.setError({ error });
                console.error(error);
                throw error;
            }
        },
    },
    getters: {
        list: (state) => Object.values(state.map),
    },
});

if (module.hot) {
    acceptHMRUpdate(useFeatureFlagsStore, module.hot);
}
